import React, { useState, useEffect, useRef } from "react";
import { useWebData } from "../context/WebDataContext"; 
import { db } from "../firebaseConfig";
import { collection, query, where, getDocs } from "firebase/firestore";
import { useNavigate } from "react-router-dom"; // 🔥 Link navigation
import Loader from "../components/Loader";
import * as XLSX from "xlsx";

function OrgHistory() {
  const navigate = useNavigate();
  
  // 🔥 Context for Search Suggestions
  const { organizations } = useWebData(); 

  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [orgData, setOrgData] = useState(null);
  
  // Suggestions State
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const wrapperRef = useRef(null);

  // Data States
  const [activeTab, setActiveTab] = useState("machines");
  const [machines, setMachines] = useState([]);
  const [salesVisits, setSalesVisits] = useState([]);
  const [serviceRecords, setServiceRecords] = useState([]); 
  const [demos, setDemos] = useState([]); // 🔥 Added Demos
  const [accounts, setAccounts] = useState({ dues: [], collected: [] });
  const [financials, setFinancials] = useState({ totalDue: 0, totalPaid: 0 });

  // Popup State
  const [viewItem, setViewItem] = useState(null);

  // ==========================================
  // 🔥 1. SMART SUGGESTIONS
  // ==========================================
  useEffect(() => {
      function handleClickOutside(event) {
          if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
              setShowSuggestions(false);
          }
      }
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [wrapperRef]);

  const handleInputChange = (e) => {
      const txt = e.target.value;
      setSearchTerm(txt);

      if (txt.length > 1 && organizations) {
          const matches = organizations
              .filter(o => o.name && o.name.toLowerCase().includes(txt.toLowerCase()))
              .map(o => o.name);
          
          setSuggestions([...new Set(matches)].slice(0, 8));
          setShowSuggestions(true);
      } else {
          setShowSuggestions(false);
      }
  };

  const selectSuggestion = (val) => {
      setSearchTerm(val);
      setShowSuggestions(false);
      setTimeout(() => performSearch(val), 100); 
  };

  // ==========================================
  // 2. MASTER SEARCH LOGIC
  // ==========================================
  const handleSearch = () => performSearch(searchTerm);

  const performSearch = async (keywordRaw) => {
    if (!keywordRaw) return alert("Enter Hospital Name!");
    setLoading(true);
    setOrgData(null);

    try {
        const keyword = keywordRaw.trim();
        let foundOrg = null;

        // 1. Search in Organizations
        const qOrg = query(collection(db, "organizations"), where("name", "==", keyword));
        const snapOrg = await getDocs(qOrg);
        
        if (!snapOrg.empty) {
            foundOrg = snapOrg.docs[0].data();
        } else {
            // Backup: Search in Installations
            const qInst = query(collection(db, "installations"), where("hospital", "==", keyword));
            const snapInst = await getDocs(qInst);
            if (!snapInst.empty) {
                foundOrg = { name: keyword, city: snapInst.docs[0].data().city, type: "Derived Record" };
            }
        }

        if (!foundOrg) {
            alert("❌ Hospital not found!");
            setLoading(false);
            return;
        }
        setOrgData(foundOrg);

        // 2. FETCH MACHINES
        const qMach = query(collection(db, "installations"), where("hospital", "==", keyword));
        const snapMach = await getDocs(qMach);
        setMachines(snapMach.docs.map(d => d.data()));

        // 3. FETCH SALES VISITS
        const qSale = query(collection(db, "sales_reports"), where("hospital", "==", keyword));
        const snapSale = await getDocs(qSale);
        const sList = snapSale.docs.map(d => ({...d.data(), type: 'Visit'}));
        sList.sort((a,b) => new Date(b.createdAt) - new Date(a.createdAt));
        setSalesVisits(sList);

        // 4. FETCH SERVICE & PMS
        let svcList = [];
        const qSvc = query(collection(db, "service_reports"), where("hospitalName", "==", keyword));
        const snapSvc = await getDocs(qSvc);
        snapSvc.forEach(d => svcList.push({...d.data(), type: 'Service Ticket'}));

        const qPMS = query(collection(db, "pms_reports"), where("hospital", "==", keyword));
        const snapPMS = await getDocs(qPMS);
        snapPMS.forEach(d => svcList.push({...d.data(), type: 'PMS Check'}));
        
        svcList.sort((a,b) => new Date(b.date || b.createdAt) - new Date(a.date || a.createdAt));
        setServiceRecords(svcList);

        // 5. FETCH DEMOS (🔥 NEW)
        const qDemo = query(collection(db, "demo_notes"), where("hospital", "==", keyword));
        const snapDemo = await getDocs(qDemo);
        const dList = snapDemo.docs.map(d => d.data());
        dList.sort((a,b) => new Date(b.date) - new Date(a.date));
        setDemos(dList);

        // 6. ACCOUNTS
        const qDue = query(collection(db, "payment_dues"), where("orgName", "==", keyword));
        const snapDue = await getDocs(qDue);
        const dues = snapDue.docs.map(d => d.data());

        const qColl = query(collection(db, "payment_collections"), where("orgName", "==", keyword));
        const snapColl = await getDocs(qColl);
        const colls = snapColl.docs.map(d => d.data());

        setAccounts({ dues, collected: colls });

        // Calcs
        const tDue = dues.filter(d => d.status === 'Open').reduce((sum, i) => sum + (Number(i.amount)||0), 0);
        const tPaid = colls.reduce((sum, i) => sum + (Number(i.amount)||0), 0);
        setFinancials({ totalDue: tDue, totalPaid: tPaid });

    } catch (e) { console.error(e); alert("Error fetching data"); }
    setLoading(false);
  };

  // ==========================================
  // 3. EXCEL DOWNLOAD
  // ==========================================
  const downloadExcel = () => {
      let dataToExport = [];
      let sheetName = "Data";

      if(activeTab === 'machines') { dataToExport = machines; sheetName = "Machines"; }
      else if (activeTab === 'sales') { dataToExport = salesVisits; sheetName = "Sales_Visits"; }
      else if (activeTab === 'service') { dataToExport = serviceRecords; sheetName = "Service_Records"; }
      else if (activeTab === 'demos') { dataToExport = demos; sheetName = "Demos"; }
      else if (activeTab === 'accounts') { dataToExport = [...accounts.dues, ...accounts.collected]; sheetName = "Accounts"; }

      const ws = XLSX.utils.json_to_sheet(dataToExport);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
      XLSX.writeFile(wb, `${orgData.name}_${sheetName}.xlsx`);
  };

  const formatDate = (iso) => {
      if(!iso) return "-";
      const d = new Date(iso);
      return isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-GB');
  };

  // 🔥🔥 LINK TO ASSET HISTORY (With Data Passing) 🔥🔥
  const goToAssetHistory = (serialNo) => {
      if (!serialNo || serialNo === '-' || serialNo === 'N/A') return alert("Serial Number not available.");
      // Navigate with STATE so AssetHistory knows what to load
      navigate('/asset-history', { state: { serialNo: serialNo } });
  };

  return (
    <div style={{padding:"20px", fontFamily:"'Segoe UI', sans-serif", maxWidth:"1100px", margin:"0 auto"}}>
      
      {/* SEARCH HEADER */}
      <div style={searchBoxStyle} ref={wrapperRef}>
          <h2 style={{margin:0, color:"#2c3e50"}}>🏥 Hospital 360° View</h2>
          <div style={{position: "relative", marginTop:"15px", display:"flex", gap:"10px"}}>
              <div style={{flex: 1, position: "relative"}}>
                  <input 
                    type="text" 
                    placeholder="Search Hospital Name..." 
                    value={searchTerm} 
                    onChange={handleInputChange} 
                    onFocus={() => searchTerm.length > 1 && setShowSuggestions(true)}
                    style={inputStyle}
                  />
                  {showSuggestions && suggestions.length > 0 && (
                      <ul style={suggestionsBox}>
                          {suggestions.map((s, i) => (
                              <li key={i} onClick={() => selectSuggestion(s)} style={suggestionItem}>
                                  🏥 {s}
                              </li>
                          ))}
                      </ul>
                  )}
              </div>
              <button onClick={handleSearch} style={btnSearch}>Search</button>
          </div>
      </div>

      {loading && <Loader />}

      {orgData && (
          <div>
              {/* PROFILE CARD */}
              <div style={profileCard}>
                  <div style={{flex:1}}>
                      <h2 style={{margin:0, color:"#2c3e50"}}>{orgData.name}</h2>
                      <p style={{margin:"5px 0", color:"gray"}}>📍 {orgData.city} {orgData.state ? `, ${orgData.state}` : ''}</p>
                      <p style={{fontSize:"13px"}}>📞 {orgData.mobile || '-'} | ✉️ {orgData.email || '-'}</p>
                  </div>
                  <div style={{textAlign:"right", display:"flex", gap:"15px"}}>
                      <div style={statBox}>
                          <small>Pending Due</small>
                          <div style={{color:"#c0392b", fontSize:"22px", fontWeight:"bold"}}>₹ {financials.totalDue.toLocaleString()}</div>
                      </div>
                      <div style={statBox}>
                          <small>Total Paid</small>
                          <div style={{color:"#27ae60", fontSize:"22px", fontWeight:"bold"}}>₹ {financials.totalPaid.toLocaleString()}</div>
                      </div>
                  </div>
              </div>

              {/* TABS */}
              <div style={tabContainer}>
                  <div style={{display:"flex", gap:"10px", overflowX:"auto"}}>
                      <button onClick={()=>setActiveTab("machines")} style={activeTab==="machines"?{...tabStyle, borderBottom:"3px solid #2980b9", color:"#2980b9"}:tabStyle}>⚙️ Machines ({machines.length})</button>
                      <button onClick={()=>setActiveTab("sales")} style={activeTab==="sales"?{...tabStyle, borderBottom:"3px solid #e67e22", color:"#e67e22"}:tabStyle}>🤝 Sales ({salesVisits.length})</button>
                      <button onClick={()=>setActiveTab("service")} style={activeTab==="service"?{...tabStyle, borderBottom:"3px solid #c0392b", color:"#c0392b"}:tabStyle}>🛠️ Service ({serviceRecords.length})</button>
                      <button onClick={()=>setActiveTab("demos")} style={activeTab==="demos"?{...tabStyle, borderBottom:"3px solid #16a085", color:"#16a085"}:tabStyle}>🎭 Demos ({demos.length})</button>
                      <button onClick={()=>setActiveTab("accounts")} style={activeTab==="accounts"?{...tabStyle, borderBottom:"3px solid #27ae60", color:"#27ae60"}:tabStyle}>💰 Accounts</button>
                  </div>
                  <button onClick={downloadExcel} style={btnDownload}>📥 Download</button>
              </div>

              {/* CONTENT AREA */}
              <div style={contentArea}>
                  
                  {/* 1. MACHINES TAB */}
                  {activeTab === "machines" && (
                      <table style={tableStyle}>
                          <thead><tr style={{background:"#f0f8ff"}}><th style={thStyle}>Product</th><th style={thStyle}>Serial No</th><th style={thStyle}>Warranty</th><th style={thStyle}>Action</th></tr></thead>
                          <tbody>
                              {machines.map((m, i) => (
                                  <tr key={i}>
                                      <td style={tdStyle}><strong>{m.product}</strong></td>
                                      <td style={tdStyle}>{m.serialNo}</td>
                                      <td style={tdStyle}>{m.warrantyExpiry}</td>
                                      <td style={tdStyle}>
                                          <button onClick={()=>goToAssetHistory(m.serialNo)} style={btnLink}>🔍 Kundali</button>
                                      </td>
                                  </tr>
                              ))}
                              {machines.length===0 && <tr><td colSpan="4" style={{padding:"20px", textAlign:"center"}}>No machines installed.</td></tr>}
                          </tbody>
                      </table>
                  )}

                  {/* 2. SALES VISITS TAB */}
                  {activeTab === "sales" && (
                      <table style={tableStyle}>
                          <thead><tr style={{background:"#fff3e0"}}><th style={thStyle}>Date</th><th style={thStyle}>Employee</th><th style={thStyle}>Met With</th><th style={thStyle}>Outcome</th><th style={thStyle}>Info</th></tr></thead>
                          <tbody>
                              {salesVisits.map((v, i) => (
                                  <tr key={i}>
                                      <td style={tdStyle}>{formatDate(v.createdAt)}</td>
                                      <td style={tdStyle}>{v.userName}</td>
                                      <td style={tdStyle}>{v.person}</td>
                                      <td style={{...tdStyle, fontWeight:"bold", color: v.outcome==='Hot'?'red':'green'}}>{v.outcome}</td>
                                      <td style={tdStyle}><button onClick={()=>setViewItem(v)} style={btnIcon}>👁️</button></td>
                                  </tr>
                              ))}
                          </tbody>
                      </table>
                  )}

                  {/* 3. SERVICE TAB */}
                  {activeTab === "service" && (
                      <table style={tableStyle}>
                          <thead><tr style={{background:"#ffebee"}}><th style={thStyle}>Date</th><th style={thStyle}>Type</th><th style={thStyle}>Serial</th><th style={thStyle}>Status</th><th style={thStyle}>Action</th></tr></thead>
                          <tbody>
                              {serviceRecords.map((s, i) => (
                                  <tr key={i}>
                                      <td style={tdStyle}>{formatDate(s.date || s.createdAt)}</td>
                                      <td style={tdStyle}>
                                          <span style={{padding:"2px 6px", borderRadius:"4px", background: s.type.includes("PMS")?"#e8f5e9":"#ffebee", color: s.type.includes("PMS")?"green":"red", fontSize:"11px", fontWeight:"bold"}}>
                                              {s.type}
                                          </span>
                                      </td>
                                      <td style={tdStyle}>{s.serialNo || '-'}</td>
                                      <td style={tdStyle}>{s.status}</td>
                                      <td style={tdStyle}>
                                          <div style={{display:"flex", gap:"5px"}}>
                                              <button onClick={()=>setViewItem(s)} style={btnIcon}>👁️</button>
                                              {/* 🔥 LINK BUTTON */}
                                              {s.serialNo && <button onClick={()=>goToAssetHistory(s.serialNo)} style={btnLink}>🚀 Asset</button>}
                                          </div>
                                      </td>
                                  </tr>
                              ))}
                          </tbody>
                      </table>
                  )}

                  {/* 4. DEMOS TAB */}
                  {activeTab === "demos" && (
                      <table style={tableStyle}>
                          <thead><tr style={{background:"#e0f7fa"}}><th style={thStyle}>Date</th><th style={thStyle}>Product</th><th style={thStyle}>Serial</th><th style={thStyle}>Result</th><th style={thStyle}>Action</th></tr></thead>
                          <tbody>
                              {demos.map((d, i) => (
                                  <tr key={i}>
                                      <td style={tdStyle}>{formatDate(d.date)}</td>
                                      <td style={tdStyle}>{d.product}</td>
                                      <td style={tdStyle}>{d.serialNo || '-'}</td>
                                      <td style={{...tdStyle, fontWeight:"bold"}}>{d.result}</td>
                                      <td style={tdStyle}>
                                          <div style={{display:"flex", gap:"5px"}}>
                                              <button onClick={()=>setViewItem(d)} style={btnIcon}>👁️</button>
                                              {d.serialNo && <button onClick={()=>goToAssetHistory(d.serialNo)} style={btnLink}>🚀 Asset</button>}
                                          </div>
                                      </td>
                                  </tr>
                              ))}
                          </tbody>
                      </table>
                  )}

                  {/* 5. ACCOUNTS TAB */}
                  {activeTab === "accounts" && (
                      <div style={{display:"flex", gap:"20px", flexWrap:"wrap"}}>
                          <div style={{flex:1, minWidth:"300px"}}>
                              <h4 style={{margin:"0 0 10px 0", color:"#c0392b", borderBottom:"2px solid #c0392b", paddingBottom:"5px"}}>⏳ Pending Dues</h4>
                              {accounts.dues.filter(d=>d.status==='Open').map((d, i) => (
                                  <div key={i} style={{...visitCard, borderLeft:"4px solid #c0392b"}}>
                                      <div style={{display:"flex", justifyContent:"space-between"}}>
                                          <span style={{fontWeight:"bold", fontSize:"16px"}}>₹ {d.amount}</span>
                                          <span style={{fontSize:"12px", color:"red"}}>Due: {formatDate(d.dueDate)}</span>
                                      </div>
                                      <div style={{fontSize:"12px", color:"gray"}}>Bill: {d.billNo}</div>
                                      {d.notes && <div style={{fontSize:"11px", color:"#555", fontStyle:"italic", marginTop:"3px"}}>"{d.notes}"</div>}
                                  </div>
                              ))}
                              {accounts.dues.filter(d=>d.status==='Open').length===0 && <p style={{color:"gray", fontStyle:"italic"}}>No pending dues. Great!</p>}
                          </div>
                          
                          <div style={{flex:1, minWidth:"300px"}}>
                              <h4 style={{margin:"0 0 10px 0", color:"#27ae60", borderBottom:"2px solid #27ae60", paddingBottom:"5px"}}>✅ Payment History</h4>
                              {accounts.collected.map((c, i) => (
                                  <div key={i} style={{...visitCard, borderLeft:"4px solid #27ae60"}}>
                                      <div style={{display:"flex", justifyContent:"space-between"}}>
                                          <span style={{fontWeight:"bold", fontSize:"16px", color:"green"}}>₹ {c.amount}</span>
                                          <span style={{fontSize:"12px", color:"gray"}}>{formatDate(c.date)}</span>
                                      </div>
                                      <div style={{fontSize:"12px"}}>Via: {c.mode} ({c.userName})</div>
                                  </div>
                              ))}
                          </div>
                      </div>
                  )}

              </div>
          </div>
      )}

      {/* POPUP MODAL */}
      {viewItem && (
          <div style={modalOverlay}>
              <div style={modalContent}>
                  <h3 style={{marginTop:0, color:"#3b5998", borderBottom:"1px solid #eee", paddingBottom:"10px"}}>ℹ️ Record Details</h3>
                  <div style={{fontSize:"13px", lineHeight:"1.6"}}>
                      {/* Common Fields */}
                      {viewItem.userName && <p><strong>👤 User:</strong> {viewItem.userName}</p>}
                      {viewItem.date && <p><strong>📅 Date:</strong> {formatDate(viewItem.date)}</p>}
                      
                      {/* Sales */}
                      {viewItem.person && <p><strong>Met:</strong> {viewItem.person}</p>}
                      {viewItem.outcome && <p><strong>Result:</strong> {viewItem.outcome}</p>}
                      
                      {/* Service */}
                      {viewItem.issue && <div style={{background:"#ffebee", padding:"8px", borderRadius:"4px", marginTop:"5px"}}><strong style={{color:"red"}}>Issue:</strong> {viewItem.issue}</div>}
                      {viewItem.action && <div style={{background:"#e8f5e9", padding:"8px", borderRadius:"4px", marginTop:"5px"}}><strong style={{color:"green"}}>Action:</strong> {viewItem.action}</div>}
                      
                      {/* Demo */}
                      {viewItem.result && <p><strong>Demo Result:</strong> {viewItem.result}</p>}

                      {viewItem.remarks && <p><strong>📝 Remarks:</strong> {viewItem.remarks}</p>}
                  </div>
                  <button onClick={()=>setViewItem(null)} style={btnClose}>Close</button>
              </div>
          </div>
      )}

    </div>
  );
}

// STYLES
const searchBoxStyle = { textAlign:"center", background:"white", padding:"25px", borderRadius:"10px", boxShadow:"0 4px 10px rgba(0,0,0,0.1)", marginBottom:"20px", position:"relative", zIndex: 10 };
const inputStyle = { padding:"12px", width:"100%", borderRadius:"5px", border:"1px solid #ccc", fontSize:"16px", outline:"none", boxSizing:"border-box" };
const btnSearch = { padding:"12px 25px", backgroundColor:"#2c3e50", color:"white", border:"none", borderRadius:"5px", cursor:"pointer", fontWeight:"bold", fontSize:"14px" };
const btnDownload = { padding:"8px 15px", backgroundColor:"#2980b9", color:"white", border:"none", borderRadius:"5px", cursor:"pointer", fontSize:"12px", marginLeft:"auto" };

const profileCard = { background:"white", padding:"20px", borderRadius:"10px", boxShadow:"0 2px 5px rgba(0,0,0,0.1)", display:"flex", alignItems:"center", marginBottom:"20px" };
const statBox = { background:"#f9f9f9", padding:"10px 20px", borderRadius:"8px", textAlign:"center", minWidth:"100px" };

const tabContainer = { display:"flex", justifyContent:"space-between", borderBottom:"2px solid #ddd", marginBottom:"20px", alignItems:"center" };
const tabStyle = { padding:"12px 20px", background:"none", border:"none", cursor:"pointer", fontSize:"14px", color:"#555", fontWeight:"bold", transition:"0.2s", whiteSpace:"nowrap" };

const contentArea = { background:"white", padding:"20px", borderRadius:"10px", boxShadow:"0 2px 5px rgba(0,0,0,0.05)", minHeight:"300px" };

const tableStyle={width:"100%", borderCollapse:"collapse", fontSize:"13px"};
const thStyle={padding:"10px", border:"1px solid #ddd", textAlign:"left"};
const tdStyle={padding:"10px", border:"1px solid #ddd"};

const visitCard = { padding:"12px", border:"1px solid #eee", borderRadius:"6px", marginBottom:"10px", background:"#fff", boxShadow:"0 2px 4px rgba(0,0,0,0.05)" };

const btnLink = { padding:"5px 10px", backgroundColor:"#e67e22", color:"white", border:"none", borderRadius:"4px", cursor:"pointer", fontSize:"11px" };
const btnIcon = { padding:"5px 8px", backgroundColor:"#3498db", color:"white", border:"none", borderRadius:"4px", cursor:"pointer", fontSize:"12px" };

const suggestionsBox = {
    position: "absolute", top: "100%", left: 0, width: "100%", background: "white", 
    border: "1px solid #ddd", borderRadius: "5px", boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
    maxHeight: "200px", overflowY: "auto", listStyle: "none", padding: 0, margin: "5px 0", zIndex: 100
};
const suggestionItem = { padding: "10px", borderBottom: "1px solid #eee", cursor: "pointer", textAlign: "left", fontSize: "14px" };

const modalOverlay = { position:"fixed", top:0, left:0, width:"100%", height:"100%", background:"rgba(0,0,0,0.5)", display:"flex", justifyContent:"center", alignItems:"center", zIndex:1000 };
const modalContent = { background:"white", padding:"25px", borderRadius:"10px", width:"450px", boxShadow:"0 5px 15px rgba(0,0,0,0.3)" };
const btnClose = { marginTop:"15px", backgroundColor:"#333", color:"white", border:"none", padding:"10px", borderRadius:"4px", width:"100%", cursor:"pointer" };

export default OrgHistory;